/**
 * Une bulle de message (docs/13 §4, §6).
 *
 * ⚠️ **Le corps est du texte brut, et il le reste.** React échappe ce qu'il
 * rend ; c'est toute la protection contre le XSS stocké de docs/13 §6, et elle
 * ne tient que tant que personne n'écrit `dangerouslySetInnerHTML` ici. Le
 * markdown limité passe par `MessageMarkdown`, qui produit un **arbre typé** —
 * cinq nœuds, pas un de plus — et jamais du HTML.
 */
import { useTranslation } from "react-i18next";

import type { Message } from "@/features/messaging/api/useMessaging";
import { MessageMarkdown } from "@/features/messaging/components/MessageMarkdown";
import { Button } from "@/shared/ui/Button";

interface MessageBubbleProps {
  message: Message;
  isMine: boolean;
  onDelete: (messageId: string) => void;
  onReport: (messageId: string) => void;
}

export function MessageBubble({ message, isMine, onDelete, onReport }: MessageBubbleProps) {
  const { t, i18n } = useTranslation("messaging");
  const time = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(message.created_at));

  return (
    <li
      className={`flex flex-col gap-1 ${isMine ? "items-end" : "items-start"}`}
      data-testid="message"
      data-mine={isMine}
      data-deleted={message.is_deleted}
      data-hidden={message.is_hidden}
    >
      <div
        className={`flex max-w-[75%] flex-col gap-2 rounded-2xl px-4 py-2 text-sm ${
          isMine
            ? "bg-indigo-600 text-white"
            : "bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-100"
        }`}
      >
        {/* Deux états, deux phrases — et jamais `body === null`, qui les
            confondrait. Dire « message supprimé » à l'auteur d'un message
            retiré par la modération lui ferait croire qu'il l'a effacé
            lui-même, et chercher ce qu'il n'a pas fait. C'est l'état qui
            décide ; le corps nul n'en est que la conséquence. */}
        {message.is_hidden ? (
          <span className="italic opacity-70">{t("message.hidden")}</span>
        ) : message.is_deleted ? (
          <span className="italic opacity-70">{t("message.deleted")}</span>
        ) : (
          <MessageMarkdown body={message.body ?? ""} />
        )}
      </div>
      <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
        <time dateTime={message.created_at}>{time}</time>
        {message.edited_at !== null ? <span>{t("message.edited")}</span> : null}
        {message.is_deleted || message.is_hidden ? null : isMine ? (
          <Button
            variant="ghost"
            className="px-1 py-0 text-xs"
            onClick={() => {
              onDelete(message.id);
            }}
          >
            {t("message.delete")}
          </Button>
        ) : (
          <Button
            variant="ghost"
            className="px-1 py-0 text-xs"
            onClick={() => {
              onReport(message.id);
            }}
          >
            {t("message.report")}
          </Button>
        )}
      </div>
    </li>
  );
}
